import { Box, Loader } from '@wix/design-system';
import { WIX_STUDIO_HEADER_HEIGHT } from '@wix/core-domain-search';
import { DomainSearchSuiteModule } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { useDomainPermissions } from '@wix/my-domains-shared-components/@common-plugins/useDomainPermissions';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import React, { useMemo } from 'react';
import { ResultsLayout, ResultsRecommendations, ResultsSearchBar } from '../ResultsLayout';
import { buildMobileEntriStatusesOverrides } from './mobileEntriStatusesOverrides';
import { mobileUpgradeRequireAssignStatus } from './mobileUpgradeAssignStatus';
import { LoggedInMobileVoucherBanner } from '../../Components/VoucherBanner';
import { getLoggedInMobileStatusesConfiguration } from '../../Components';
import { SearchedDomainBroker, useSearchedDomainBroker } from '../../Components/SearchedDomainBroker';
import { useIsSiteLevelBrokerEnabled } from '../../hooks/useIsSiteLevelBrokerEnabled';
import { ExperimentSpecs } from '../../constants';
import { mobileUpgradeRequireConnectStatus } from './mobileUpgradeConnectStatus';
const SCROLL_TARGET_DATA_HOOK = 'logged-in-mobile-results-scroll-target';
export const LoggedInMobileResultsPage = _ref => {
  var _getPremiumEssentials;
  let handlers = _ref.handlers,
    _ref$externalViewer = _ref.externalViewer,
    externalViewer = _ref$externalViewer === void 0 ? false : _ref$externalViewer,
    onConnectIntro = _ref.onConnectIntro,
    onConnectManual = _ref.onConnectManual,
    onConnectError = _ref.onConnectError;
  const isConnectEnabled = Boolean((_getPremiumEssentials = getPremiumEssentialsSafe()) == null || (_getPremiumEssentials = _getPremiumEssentials.experiments) == null ? void 0 : _getPremiumEssentials[ExperimentSpecs.LoggedInMobileConnect]);
  const customConfiguration = useMemo(() => getLoggedInMobileStatusesConfiguration({
    isConnectEnabled
  }), [isConnectEnabled]);

  // Override the Entri-starting actions themselves (not just their cards) so
  // clicking a results card runs the mobile full-page divert — the same actions
  // `SelectSiteRoute` runs imperatively after a site is picked.
  const statusesOverrides = useMemo(() => isConnectEnabled ? buildMobileEntriStatusesOverrides({
    onConnectIntro,
    onConnectManual,
    onConnectError
  }) : {
    upgradeRequireAssign: mobileUpgradeRequireAssignStatus,
    upgradeRequireConnect: mobileUpgradeRequireConnectStatus
  }, [isConnectEnabled, onConnectIntro, onConnectManual, onConnectError]);
  return /*#__PURE__*/React.createElement(DomainsApiClient.Provider, null, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      siteInfo: {
        externalViewer
      },
      handlers,
      statusesOverrides,
      resolveCorrectedDomains: true
    },
    statusesCustomComponents: {
      customConfiguration
    }
  }, /*#__PURE__*/React.createElement(LoggedInMobileResultsPageContent, null)));
};
const LoggedInMobileResultsPageContent = () => {
  const _useDomainPermissions = useDomainPermissions(),
    canManageDomains = _useDomainPermissions.canManageDomains,
    isLoading = _useDomainPermissions.isLoading,
    error = _useDomainPermissions.error,
    refetch = _useDomainPermissions.refetch;
  const canRenderResults = !isLoading && !error && Boolean(canManageDomains());
  const brokerDomainName = useSearchedDomainBroker({
    enabled: useIsSiteLevelBrokerEnabled() && canRenderResults
  });
  if (isLoading) {
    return /*#__PURE__*/React.createElement(Box, {
      height: `calc(100dvh - ${WIX_STUDIO_HEADER_HEIGHT})`,
      align: "center",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, null));
  }
  if (error) {
    return /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.TechnicalIssue, {
      onRetry: () => refetch()
    });
  }
  if (!canManageDomains()) {
    return /*#__PURE__*/React.createElement(Box, {
      height: `calc(100dvh - ${WIX_STUDIO_HEADER_HEIGHT})`
    }, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.NoPermissionsBlock, null));
  }
  return /*#__PURE__*/React.createElement(ResultsLayout, {
    dataHook: SCROLL_TARGET_DATA_HOOK,
    biViewMode: "Gate Result Page",
    reportGatePageLoadEvents: false,
    useReferralAdditionalInfoAsSearchOrigin: true,
    querySubmitPath: "/results",
    deviceType: "Smartphone",
    stickyHeaderOffset: WIX_STUDIO_HEADER_HEIGHT,
    hideSearchBarShadowOnPartialMatch: true,
    showSearchIcon: true,
    hideFilterChips: true,
    useCompactRecommendationsLayout: true,
    headerMargin: "0",
    voucherPlacement: "outsideStickyHeader",
    mobileVoucherInSearchBar: true,
    searchBar: /*#__PURE__*/React.createElement(ResultsSearchBar, {
      showUnassignedDomainsOnEmptyInput: true,
      compactLayouts: true,
      scrollIntoView: {
        dataHook: SCROLL_TARGET_DATA_HOOK,
        scrollMarginTop: WIX_STUDIO_HEADER_HEIGHT
      }
    }),
    voucher: /*#__PURE__*/React.createElement(LoggedInMobileVoucherBanner, {
      isOptimized: true
    })
  }, /*#__PURE__*/React.createElement(ResultsRecommendations, {
    showHeroCard: true,
    hideAiInsights: true,
    titleOverflowStrategy: "scale",
    rowTitleOverflowStrategy: "wrap",
    clickableRows: true,
    compactPriceLabel: true,
    stackedMobileCards: true,
    aboveTopResult: brokerDomainName && /*#__PURE__*/React.createElement(SearchedDomainBroker, {
      domainName: brokerDomainName
    })
  }));
};
//# sourceMappingURL=LoggedInMobileResultsPage.js.map